import { Guid, ObjectType } from '@models/base';
import { IVzFilePreview, VzFile } from '@models/files/file';
import { UrlPreview } from '@models/shared/url-preview';
import { VzTag } from '@models/tags/tag';
import { TaskTypeDef } from '@models/tasks/task-type-def';
import { Workflow } from '@models/workflow/workflow';
import { WorkflowBrief } from '@models/workflow/workflow-brief';
import { WorkflowTriggerActionDef } from '@models/workflow/workflow-trigger-action-def';
import { VzParam } from '@models/params/param';

import { Observable, Subject} from 'rxjs';

import { HttpApiError } from '@models/api/http-api-error';
import { KanbanBoard } from '@models/tasks/kanban-board';
import { Project } from '@models/projects/project';
import { Task } from '@models/tasks/task';
import { TasksFilter } from '@models/tasks/tasks-filter';
import { Chat } from '@models/chats/chat';
import { UserOrgStatus } from '@models/users/user-org-status';
import { Milestone } from '@models/projects/milestone';

export const CacheCats = [
    '_any_',
    'tag',
    'file',
    'preview',
    'workflow',
    'taskType',
    'urlPreview',
    'kanban',
    'task',
    'project',
    'tfilter',
    'chat',
    'userStatus',
    'milestone',
] as const;
export type TCacheCats = typeof CacheCats[number];

export const CacheCatsAny = ['wfConstraints', 'wfTriggers', 'wfActions', 'systemTaskTypes'] as const;
export type TCacheCatsAny = typeof CacheCatsAny[number];
export const CashCatClassAny = {
    wfConstraints: VzParam,
    wfTriggers: VzParam,
    wfActions: WorkflowTriggerActionDef,
    systemTaskTypes: TaskTypeDef,
}
export type TCacheStoredCatAnyType = {
    wfConstraints: VzParam[],
    wfTriggers: VzParam[],
    wfActions: WorkflowTriggerActionDef[],
    systemTaskTypes: TaskTypeDef[],
}

export type TCacheCatType = {
    _any_: any,
    tag: VzTag,
    file: VzFile,
    preview: IVzFilePreview,
    workflow: Workflow | WorkflowBrief,
    taskType: TaskTypeDef,
    urlPreview: UrlPreview,
    kanban: KanbanBoard,
    task: Task,
    project: Project,
    tfilter: TasksFilter,
    chat: Chat,
    userStatus: UserOrgStatus,
    milestone: Milestone,
};

export const ObjectType2CacheCat: { [type: string]: TCacheCats } = {
    [ObjectType.Tag]: 'tag',
    [ObjectType.File]: 'file',
    [ObjectType.Workflow]: 'workflow',
    [ObjectType.Tasktype]: 'taskType',
    [ObjectType.UrlPreview]: 'urlPreview',
    [ObjectType.KanbanBoard]: 'kanban',
    [ObjectType.Task]: 'task',
    [ObjectType.Project]: 'project',
    [ObjectType.TasksFilter]: 'tfilter',
    [ObjectType.Chat]: 'chat',
    [ObjectType.UserOrgStatus]: 'userStatus',
    [ObjectType.Milestone]: 'milestone',
};

export enum CacheState {
    Unknown = 0,
    Init = 1,
    Ready = 5,
    Error = 100,
}

export interface ICacheBaseItem {
    dt: number;
    err?: HttpApiError;
    loading?: boolean;
    deleted?: boolean,
}

export interface ICacheLink {
    t: TCacheCats;
    id: string;
}

export interface ICacheItemBase<T> extends ICacheBaseItem {
    id: string;
    item?: T;
}
export type ICacheItem<T extends TCacheCats> = ICacheItemBase<TCacheCatType[T]>;
export interface ICacheArrayBase<T> extends ICacheBaseItem {
    items?: T[];
}
export type ICacheArray<T extends TCacheCats> = ICacheArrayBase<TCacheCatType[T]>;


export interface ICacheStoredItem<T extends TCacheCats> extends ICacheItemBase<TCacheCatType[T]> {
    la: number;
    apiReq?: Observable<ICacheItemBase<TCacheCatType[T]>>;
    arrayApiReq?: Observable<ICacheItemBase<TCacheCatType[T]>[]>;
    projectApiReq?: Observable<ICacheArrayBase<T>>;
    listApiReq?: Observable<ICacheArrayBase<T>>;
    getting?: Subject<void>;
}

export type TCacheCatDef<T extends TCacheCats> = {
    init?: () => Observable<TCacheCatType[T][] | undefined>,
    api?: {
        get?: (id: Guid) => Observable<TCacheCatType[T] | undefined>,
        getArray?: (ids: Guid[]) => Observable<TCacheCatType[T][] | undefined>,
        getArray4project?: (pid: Guid) => Observable<TCacheCatType[T][] | undefined>,
        getFullList?: () => Observable<TCacheCatType[T][] | undefined>,
    },
    db?: {
        get: (id: Guid) => Promise<TCacheCatType[T] | undefined>,
        getArray: (ids: Guid[]) => Promise<TCacheCatType[T][] | undefined>,
        getArray4project?: (pid: Guid) => Promise<TCacheCatType[T][] | undefined>,
        getFullList?: () => Promise<TCacheCatType[T][] | undefined>,
        put: (v: TCacheCatType[T][]) => Promise<void>,
        putFilter?: (v?: TCacheCatType[T]) => boolean,
        remove: (ids: Guid[]) => Promise<void>,
        clear: (untrusted?: boolean) => Promise<void>,
    },
    mem?: {
        ttl: number | ((id: string) => number),                     // in seconds
        putFilter?: (v?: TCacheCatType[T]) => boolean,
    },
    linkedTo?: (item: TCacheCatType[T]) => ICacheLink[] | void,     // retun array of items this item is depends on (and will be updated along with that items)
    on?: {
        update?: (items: TCacheCatType[T][]) => void,
        remove?: (ids: string[]) => void,
    },
    transformId?: (id: string) => string | undefined,
    eventsTrack?: boolean,                                          // track RMQ events and update items accordinally
    untrusted?: boolean,                                            // clear cache of this category on connection (syncronization) lost
    _hasFullProj?: { [pid: Guid]: boolean },                        // if defined (empty {}) - allow to get, store and track ALL items for a single project
    _hasFull?: boolean,                                             // if defined (== false) - allow to get, store and track ALL items of that kind
}

export const InitCacheItem: { [T in TCacheCats]: (v: any) => TCacheCatType[T] } = {
    _any_: v => v,
    preview: v => v,
    tag: v => new VzTag(v),
    file: v => new VzFile(v),
    workflow: v => new Workflow(v),
    taskType: v => new TaskTypeDef(v),
    urlPreview: v => new UrlPreview(v),
    kanban: v => new KanbanBoard(v),
    task: v => new Task(v),
    project: v => new Project(v),
    tfilter: v => new TasksFilter(v),
    chat: v => new Chat(v),
    userStatus: v => new UserOrgStatus(v),
    milestone: v => new Milestone(v),
};
